import { COMMERCIAL_DOCUMENT_LABELS } from "../../shared/commercial-documents";
import { hubContract, type HubDocument } from "../../shared/tool-hubs";
import { documentDate, documentMoney } from "../knowledge/customer-document-format";

/** A Unix-seconds timestamp as a calendar day, such as "Sep 23, 2026". */
export function dayOf(seconds: number) {
  return documentDate(new Date(seconds * 1000).toISOString().slice(0, 10));
}

/** One line about a tool's contract: the order form's state and any change orders. */
export function contractLine(documents: readonly HubDocument[] | null) {
  if (documents === null) return null;
  const { orderForm, changeOrders } = hubContract(documents);
  if (!orderForm) return "No order form yet";
  const state = orderForm.filing
    ? `signed ${documentDate(orderForm.filing.signedOn)}`
    : `version ${orderForm.revision}, not signed`;
  const changes = changeOrders.length
    ? ` · ${changeOrders.length} change ${changeOrders.length === 1 ? "order" : "orders"}`
    : "";
  return `${COMMERCIAL_DOCUMENT_LABELS.order_form} ${orderForm.reference} ${state}${changes}`;
}

type BillingFacts = {
  terms: readonly {
    status: string;
    totals: { oneTimeMinor: number; monthlyMinor: number; annualMinor: number };
  }[];
  invoices: readonly { amountPaidMinor: number; amountRefundedMinor: number }[];
  outstandingMinor: number;
  pastDueCount: number;
};

/** What approved terms add up to, such as "$2,999 a year". */
export function agreedLine(billing: BillingFacts) {
  const approved = billing.terms.filter((terms) => terms.status === "approved");
  const sum = (pick: (totals: BillingFacts["terms"][number]["totals"]) => number) =>
    approved.reduce((total, terms) => total + pick(terms.totals), 0);
  const parts = [
    [sum((totals) => totals.annualMinor), "a year"],
    [sum((totals) => totals.monthlyMinor), "a month"],
    [sum((totals) => totals.oneTimeMinor), "one-time"],
  ] as const;
  const text = parts
    .filter(([amount]) => amount > 0)
    .map(([amount, suffix]) => `${documentMoney(amount)} ${suffix}`)
    .join(" + ");
  return text || "No approved terms";
}

export function collectedMinor(billing: BillingFacts) {
  return billing.invoices.reduce(
    (total, invoice) => total + invoice.amountPaidMinor - invoice.amountRefundedMinor,
    0,
  );
}

export function pastDueLine(billing: BillingFacts) {
  if (billing.pastDueCount === 0) return "Nothing";
  return `${billing.pastDueCount} ${billing.pastDueCount === 1 ? "invoice" : "invoices"}`;
}

/** A trimmed text field from a submitted form; files and missing fields read as empty. */
export function formText(form: FormData, name: string) {
  const value = form.get(name);
  return typeof value === "string" ? value.trim() : "";
}
